"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import AppSelect from "../../components/AppSelect";
import AssistantOnboardingHeader from "../../components/AssistantOnboardingHeader";
import Modal from "../../components/Modal";
import providersService from "../../api/services/providersService";
import useEditFlow, { clearEditFlowFlag } from "../../hooks/useEditFlow";
import { getStoredUserIdentifier } from "../../utils/authSession";
import { useToast } from "../../hooks/useToast";

const securityPricingFields = [
  { key: "securityService", label: "MOPOL (Mobile Police)" },
  { key: "miceShuttlePrice", label: "Police Officer" },
  { key: "pickUpPrice", label: "Army Officer" },
];

const vehiclePricingFields = [
  { key: "sedan", label: "Sedan" },
  { key: "suv", label: "SUV" },
  { key: "truck", label: "Truck" },
];

const defaultPricingDetails = {
  securityService: "",
  miceShuttlePrice: "",
  pickUpPrice: "",
  sedan: "",
  suv: "",
  truck: "",
};

const sanitizeCurrencyInput = (value = "") => {
  const sanitized = String(value).replace(/[^\d.]/g, "");
  const [whole = "", decimal = ""] = sanitized.split(".");
  if (sanitized.includes(".")) {
    return `${whole}.${decimal.slice(0, 2)}`;
  }
  return whole;
};

const parseCurrencyValue = (value = "") => {
  const parsed = Number(String(value).replace(/[^\d.]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const incrementCurrencyValue = (value = "", step = 100) => {
  const nextValue = parseCurrencyValue(value) + step;
  return String(nextValue);
};

const decrementCurrencyValue = (value = "", step = 100) => {
  const nextValue = Math.max(0, parseCurrencyValue(value) - step);
  return nextValue > 0 ? String(nextValue) : "";
};

function StepperCurrencyInput({ value, onChange, placeholder = "₦0.00" }) {
  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(sanitizeCurrencyInput(event.target.value))}
        placeholder={placeholder}
        inputMode="decimal"
        className="w-full rounded-lg border border-[#d8dbe0] bg-white px-4 py-3 pr-12 text-base font-medium text-gray-800 placeholder:text-gray-400 focus:border-[#7a0051] focus:outline-none focus:ring-1 focus:ring-[#7a0051]/30"
      />
      <div className="absolute inset-y-0 right-3 flex flex-col items-center justify-center gap-0.5">
        <button
          type="button"
          onClick={() => onChange(incrementCurrencyValue(value))}
          className="flex h-4 w-4 items-center justify-center text-[#98A2B3] transition hover:text-[#344054]"
          aria-label="Increase amount"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onChange(decrementCurrencyValue(value))}
          className="flex h-4 w-4 items-center justify-center text-[#98A2B3] transition hover:text-[#344054]"
          aria-label="Decrease amount"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function InlineRateRow({ label, value, onChange }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_8.5rem] items-center gap-3 sm:grid-cols-[minmax(0,1fr)_13rem] sm:gap-5">
      <label className="text-base font-medium leading-snug text-gray-800">
        {label}
      </label>
      <StepperCurrencyInput value={value} onChange={onChange} />
    </div>
  );
}

export default function PricingAccount({ currentUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const fromProfile = Boolean(location.state?.fromProfile);
  const isEditingProfile = useEditFlow(fromProfile);
  const toast = useToast();

  const userIdentifier = currentUser?.id || currentUser?.email || getStoredUserIdentifier() || "guest";
  const storageKey = `assistantPricingDraft_${userIdentifier}`;

  const [pricePerDay, setPricePerDay] = useState("");
  const [pricingDetails, setPricingDetails] = useState(defaultPricingDetails);
  const [bank, setBank] = useState("");
  const [banks, setBanks] = useState([]);
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingBanks, setIsLoadingBanks] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [resolveError, setResolveError] = useState("");
  const [bankNotice, setBankNotice] = useState("");
  const [isHydrated, setIsHydrated] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [hasLoadedPayment, setHasLoadedPayment] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const fallbackBanks = useMemo(
    () => [
      { name: "Access Bank", code: "044", currency: "NGN" },
      { name: "GTBank", code: "058", currency: "NGN" },
      { name: "Zenith Bank", code: "057", currency: "NGN" },
      { name: "UBA", code: "033", currency: "NGN" },
      { name: "First Bank", code: "011", currency: "NGN" },
      { name: "Sterling Bank", code: "232", currency: "NGN" },
      { name: "Kuda", code: "50211", currency: "NGN" },
      { name: "Opay", code: "999992", currency: "NGN" },
    ],
    []
  );

  useEffect(() => {
    let active = true;
    const fetchBanks = async () => {
      setIsLoadingBanks(true);
      try {
        const response = await providersService.getBanks();
        const list = response?.banks || response?.data?.banks || [];
        if (!active) return;
        setBanks(list);
        setBankNotice("");
      } catch {
        if (!active) return;
        setBanks(fallbackBanks);
        setBankNotice("Bank list is temporarily unavailable. Showing a fallback list.");
      } finally {
        if (active) setIsLoadingBanks(false);
      }
    };

    fetchBanks();
    return () => {
      active = false;
    };
  }, [fallbackBanks]);

  useEffect(() => {
    if (isEditingProfile) {
      setIsHydrated(true);
      setHasDraft(false);
      return;
    }

    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      setPricePerDay("");
      setPricingDetails(defaultPricingDetails);
      setBank("");
      setAccountNumber("");
      setAccountName("");
      setHasDraft(false);
      setIsHydrated(true);
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      setPricePerDay(parsed.pricePerDay || "");
      setPricingDetails({
        ...defaultPricingDetails,
        ...(parsed.pricingDetails || {}),
      });
      setBank(parsed.bank || "");
      setAccountNumber(parsed.accountNumber || "");
      setAccountName(parsed.accountName || "");
      setHasDraft(true);
    } catch {
      // ignore
    } finally {
      setIsHydrated(true);
    }
  }, [isEditingProfile, storageKey]);

  const normalizeBankName = useCallback(
    (bankName) => {
      if (!bankName) return "";
      const raw = String(bankName).trim();
      const match = banks.find(
        (item) => String(item.name).toLowerCase() === raw.toLowerCase()
      );
      return match?.name || raw;
    },
    [banks]
  );

  useEffect(() => {
    let active = true;

    const fetchPayment = async () => {
      try {
        const profile = await providersService.getProfile();

        const payment =
          profile?.payment ||
          profile?.pricingAndAccount ||
          profile?.pricing ||
          profile?.bankAccount ||
          profile?.accountDetails ||
          profile?.bank ||
          {};

        if (!active) return;

        const priceCandidate =
          payment?.pricePerDay ??
          payment?.price_per_day ??
          payment?.price ??
          profile?.pricePerDay ??
          profile?.price_per_day ??
          profile?.price ??
          profile?.pricing?.pricePerDay ??
          profile?.pricing?.price_per_day ??
          profile?.pricing?.price ??
          profile?.pricingAndAccount?.pricePerDay ??
          profile?.pricingAndAccount?.price_per_day ??
          profile?.pricingAndAccount?.price ??
          profile?.bankAccount?.pricePerDay ??
          profile?.bankAccount?.price_per_day;

        if (priceCandidate != null && priceCandidate !== "") {
          setPricePerDay(String(priceCandidate));
        }

        setPricingDetails((prev) => ({
          ...prev,
          securityService:
            payment?.securityService ?? payment?.mopolRate ?? profile?.pricing?.securityService ?? prev.securityService,
          miceShuttlePrice:
            payment?.miceShuttlePrice ?? payment?.policeOfficerRate ?? profile?.pricing?.miceShuttlePrice ?? prev.miceShuttlePrice,
          pickUpPrice:
            payment?.pickUpPrice ?? payment?.armyOfficerRate ?? profile?.pricing?.pickUpPrice ?? prev.pickUpPrice,
          sedan: payment?.sedan ?? payment?.sedanRate ?? profile?.pricing?.sedan ?? prev.sedan,
          suv: payment?.suv ?? payment?.suvRate ?? profile?.pricing?.suv ?? prev.suv,
          truck:
            payment?.truck ??
            payment?.truckRate ??
            payment?.bus ??
            payment?.busRate ??
            profile?.pricing?.truck ??
            prev.truck,
        }));

        if (payment?.accountNumber) setAccountNumber(String(payment.accountNumber));
        if (payment?.accountName) setAccountName(String(payment.accountName));

        const bankName = payment?.bankName || payment?.bank || payment?.bank_name || "";
        if (bankName) setBank(normalizeBankName(bankName));
      } catch {
        // ignore
      } finally {
        if (active) setHasLoadedPayment(true);
      }
    };

    const banksReady = banks.length > 0 || !isLoadingBanks;
    if (!banksReady) return;

    if (isEditingProfile) {
      fetchPayment();
      return () => {
        active = false;
      };
    }

    if (!isHydrated || hasDraft || hasLoadedPayment) return;

    fetchPayment();
    return () => {
      active = false;
    };
  }, [
    isEditingProfile,
    isHydrated,
    hasDraft,
    hasLoadedPayment,
    banks,
    isLoadingBanks,
    normalizeBankName,
  ]);

  useEffect(() => {
    if (!isHydrated || isEditingProfile) return;
    const draft = { pricePerDay, pricingDetails, bank, accountNumber, accountName };
    localStorage.setItem(storageKey, JSON.stringify(draft));
  }, [
    pricePerDay,
    pricingDetails,
    bank,
    accountNumber,
    accountName,
    isHydrated,
    storageKey,
    isEditingProfile,
  ]);

  useEffect(() => {
    let active = true;
    const selectedBank = banks.find((item) => item.name === bank) || {};
    const sanitizedAccount = accountNumber.trim();

    if (!selectedBank.code || sanitizedAccount.length < 10) {
      setResolveError("");
      setAccountName("");
      return;
    }

    const timer = setTimeout(async () => {
      setIsResolving(true);
      setResolveError("");
      try {
        const response = await providersService.resolveBankAccount({
          accountNumber: sanitizedAccount,
          bankCode: selectedBank.code,
        });
        const resolvedName = response?.accountName || response?.data?.accountName || "";
        if (active) setAccountName(resolvedName);
      } catch (err) {
        if (active) {
          setAccountName("");
          setResolveError(
            err?.response?.data?.message ||
              err?.response?.data?.error ||
              err?.message ||
              "Unable to resolve account name."
          );
        }
      } finally {
        if (active) setIsResolving(false);
      }
    }, 500);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [accountNumber, bank, banks]);

  const parsedPrice = parseCurrencyValue(pricePerDay);
  const isFormComplete =
    Boolean(pricePerDay) &&
    Boolean(bank) &&
    Boolean(accountNumber.trim()) &&
    Boolean(accountName.trim()) &&
    Number.isFinite(parsedPrice) &&
    parsedPrice > 0;

  const handleContinue = async () => {
    if (!isFormComplete) {
      toast.error("Please fill out all fields.");
      return;
    }

    setIsSubmitting(true);

    const selectedBank = banks.find((item) => item.name === bank) || {};

    try {
      const payload = {
        pricePerDay: parsedPrice,
        accountNumber: accountNumber.trim(),
        accountName: accountName.trim(),
        bankCode: selectedBank.code || "",
        bankName: selectedBank.name || bank,
        securityService: parseCurrencyValue(pricingDetails.securityService),
        miceShuttlePrice: parseCurrencyValue(pricingDetails.miceShuttlePrice),
        pickUpPrice: parseCurrencyValue(pricingDetails.pickUpPrice),
        sedan: parseCurrencyValue(pricingDetails.sedan),
        suv: parseCurrencyValue(pricingDetails.suv),
        truck: parseCurrencyValue(pricingDetails.truck),
      };

      if (isEditingProfile) {
        await providersService.updatePayment(payload);
      } else {
        await providersService.submitPayment(payload);
      }

      localStorage.removeItem(storageKey);
      setIsSuccessModalOpen(true);
    } catch (err) {
      toast.apiError(err, "Unable to submit payment details. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessDone = () => {
    setIsSuccessModalOpen(false);
    clearEditFlowFlag();
    navigate(
      isEditingProfile
        ? "/dashboards/assistant/profile"
        : "/dashboards/assistant/overview",
      {
        state: isEditingProfile
          ? undefined
          : { assistantRegistrationSubmitted: true },
      }
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-gray-900">
      <AssistantOnboardingHeader />

      <main className="mx-auto flex max-w-3xl flex-col px-4 pb-16 pt-8 sm:px-6 sm:pt-10">
        <div className="w-full space-y-5 text-center">
          <h1 className="text-2xl font-semibold text-gray-900">Pricing &amp; Account</h1>
          <div className="h-2 w-full overflow-hidden rounded-full bg-[#d3d6dd]">
            <div className="h-full w-full rounded-full bg-[#7a0051]" />
          </div>
          <p className="text-md font-medium text-gray-600">
            Set your service rate and payout below.
          </p>
        </div>

        <div className="mt-8 rounded-2xl border border-[#EAECF0] bg-white p-5 shadow-[0_12px_32px_rgba(16,24,40,0.06)] sm:mt-10 sm:p-6">
          <div className="space-y-8">
            <div className="flex items-center gap-3 rounded-xl bg-[#FFF6E8] px-4 py-4 text-left text-[#F79009]">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm font-medium sm:text-base">
                All price are set for 12 hours booking.
              </p>
            </div>

            <div className="space-y-3">
              <label className="font-medium text-gray-800">
                12-Hour Rate
              </label>
              <StepperCurrencyInput
                value={pricePerDay}
                onChange={setPricePerDay}
                placeholder="₦1,000"
              />
            </div>

            <div className="space-y-5">
              <h3 className="text-xl font-semibold text-gray-900">
                Security Service
              </h3>
              <div className="space-y-4 sm:space-y-5">
                {securityPricingFields.map((field) => (
                  <InlineRateRow
                    key={field.key}
                    label={field.label}
                    value={pricingDetails[field.key]}
                    onChange={(value) =>
                      setPricingDetails((prev) => ({
                        ...prev,
                        [field.key]: value,
                      }))
                    }
                  />
                ))}
              </div>
            </div>

            <div className="space-y-5">
              <h3 className="text-xl font-semibold text-gray-900">
                Vehicles
              </h3>
              <div className="space-y-4 sm:space-y-5">
                {vehiclePricingFields.map((field) => (
                  <InlineRateRow
                    key={field.key}
                    label={field.label}
                    value={pricingDetails[field.key]}
                    onChange={(value) =>
                      setPricingDetails((prev) => ({
                        ...prev,
                        [field.key]: value,
                      }))
                    }
                  />
                ))}
              </div>
            </div>

            <div className="h-px bg-[#D0D5DD]" />

            <div className="space-y-5">
              <h3 className="text-xl font-semibold text-gray-900">
                Set Withdrawal Bank
              </h3>

              <div className="space-y-2">
                <label className="font-medium text-gray-800">Bank</label>
                <AppSelect
                  value={bank}
                  onChange={(value) => {
                    setBank(value);
                    setResolveError("");
                  }}
                  options={banks.map((item) => item.name)}
                  placeholder={isLoadingBanks ? "Loading banks..." : "Select Bank"}
                  buttonClassName="!rounded-lg !px-4 !py-3 !text-base !font-medium !text-gray-800"
                  optionsClassName="!rounded-xl"
                  optionClassName="!text-sm"
                />
                {bankNotice ? (
                  <p className="text-sm font-medium text-[#F79009] sm:text-base">{bankNotice}</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <label className="font-medium text-gray-800">Account Number</label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(event) => {
                    setAccountNumber(event.target.value.replace(/\D/g, "").slice(0, 10));
                    setResolveError("");
                  }}
                  placeholder="1234567890"
                  inputMode="numeric"
                  className="w-full rounded-lg border border-[#d8dbe0] bg-white px-4 py-3 text-base font-medium text-gray-800 placeholder:text-gray-400 focus:border-[#7a0051] focus:outline-none focus:ring-1 focus:ring-[#7a0051]/30"
                />
                <p className="min-h-[1.5rem] text-right text-sm font-medium text-[#667085]">
                  {isResolving ? "Resolving account name..." : accountName || ""}
                </p>
                {resolveError ? (
                  <div className="flex items-center gap-2 text-sm font-medium text-[#D92D20]">
                    <AlertCircle className="h-4 w-4" />
                    <span>{resolveError}</span>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col gap-3 pt-2">
              <button
                type="button"
                onClick={handleContinue}
                className={`w-full rounded-lg px-4 py-3 text-center text-base font-semibold text-white shadow-sm transition ${
                  isFormComplete ? "bg-[#61013B] hover:bg-[#4f0130]" : "bg-[#caa8b8] hover:bg-[#b792a3]"
                }`}
                disabled={!isFormComplete || isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Continue"}
              </button>

              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-full rounded-lg border border-[#7a0051] px-4 py-3 text-center text-base font-semibold text-[#7a0051] transition hover:bg-[#f7eef4]"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </main>

      <Modal
        isOpen={isSuccessModalOpen}
        onClose={handleSuccessDone}
        panelClassName="!max-w-2xl !rounded-[24px] !px-6 !py-8 sm:!px-10 sm:!py-10"
        showClose={false}
      >
        <div className="space-y-6 text-center sm:space-y-7">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#E9F5EA] sm:h-24 sm:w-24">
            <CheckCircle2 className="h-10 w-10 text-[#03840C] sm:h-12 sm:w-12" strokeWidth={1.8} />
          </div>
          <div className="mx-auto max-w-xl space-y-3 sm:space-y-4">
            <h2 className="text-2xl font-semibold text-[#61013B] sm:text-[2.5rem]">
              You're All Set!
            </h2>
            <p className="mx-auto max-w-lg text-base font-medium leading-relaxed text-[#667085] sm:text-xl">
              We're reviewing your account, you will receive an email once its approved.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSuccessDone}
            className="mx-auto block w-full max-w-xl rounded-[18px] border-2 border-[#61013B] px-4 py-3 text-center text-lg font-medium text-[#61013B] transition hover:bg-[#f7eef4] sm:py-4 sm:text-2xl"
          >
            Done
          </button>
        </div>
      </Modal>
    </div>
  );
}
